import { z } from 'zod'

// Imported HTML email templates (ADR 0042): a design made in Canva (Share →
// Mailchimp → "Download HTML and images"), uploaded as its zip, made safe
// for email and kept as an immutable template version. Hub's locked footer
// is still added by the renderer (comms-send.ts), never by the design.
//
// Shared by the web Worker (which unzips, hosts the images and makes the
// first pass), Platform Core (which checks the result again before storing
// it, and renders it) and the staff UI (which shows the report).
//
// The sanitizer is an allowlist over a small tokenizer, not a regex filter:
// every element and attribute that comes out is one this file names, every
// value is re-escaped, the output is balanced (a stray </table> can't close
// the wrapper the design sits in), and every style rule is scoped under the
// design's own wrapper so nothing in the design can restyle or hide the
// footer.

// ------------------------------------------------------------------ limits

export const HTML_IMPORT_LIMITS = {
  zipBytes: 15 * 1024 * 1024,
  entries: 400,
  // Uncompressed, all entries read, and any one entry.
  totalBytes: 60 * 1024 * 1024,
  entryBytes: 12 * 1024 * 1024,
  // A file this much bigger than its compressed size is treated as a bomb.
  ratio: 200,
  htmlBytes: 2 * 1024 * 1024,
  imageBytes: 5 * 1024 * 1024,
  images: 80,
  // What Hub stores after cleaning.
  storedHtmlBytes: 400 * 1024,
  storedStyleBytes: 60 * 1024,
  textChars: 60_000,
} as const

// Gmail clips a message whose HTML is over about 102 KB ("[Message
// clipped] View entire message"): the footer and its unsubscribe link fall
// below the cut.
export const GMAIL_CLIP_BYTES = 102 * 1024
export const SIZE_WARN_BYTES = 90 * 1024

// ------------------------------------------------------------------ images

export const EMAIL_IMAGE_EXTS = ['png', 'jpg', 'gif', 'webp'] as const
export type EmailImageExt = (typeof EMAIL_IMAGE_EXTS)[number]
export const EMAIL_IMAGE_TYPES: Record<EmailImageExt, string> = { png: 'image/png', jpg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' }
// The public path an image is served at, by the SHA-256 of its bytes and
// the type its bytes say it is: /e/img/<64 hex>.<ext>.
export const EMAIL_IMAGE_PATH = /^\/e\/img\/([0-9a-f]{64})\.(png|jpg|gif|webp)$/
export const emailImageKey = (sha256: string, ext: EmailImageExt) => `img/${sha256}.${ext}`
export const emailImageUrl = (origin: string, sha256: string, ext: EmailImageExt) => `${origin.replace(/\/+$/, '')}/e/img/${sha256}.${ext}`

// The type an image's bytes say it is, never its name's. SVG is never an
// email image here (it can carry script).
export function sniffImage(bytes: Uint8Array): EmailImageExt | null {
  const starts = (...sig: number[]) => bytes.length >= sig.length && sig.every((b, i) => bytes[i] === b)
  if (starts(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'png'
  if (starts(0xff, 0xd8, 0xff)) return 'jpg'
  if (starts(0x47, 0x49, 0x46, 0x38) && (bytes[4] === 0x37 || bytes[4] === 0x39) && bytes[5] === 0x61) return 'gif'
  if (starts(0x52, 0x49, 0x46, 0x46) && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return 'webp'
  return null
}

// Width and height from the image's own header, when it says.
export function imageSize(bytes: Uint8Array, ext: EmailImageExt): { width: number; height: number } | null {
  const u16be = (i: number) => (bytes[i]! << 8) | bytes[i + 1]!
  const u16le = (i: number) => bytes[i]! | (bytes[i + 1]! << 8)
  const u32be = (i: number) => ((bytes[i]! << 24) >>> 0) + (bytes[i + 1]! << 16) + (bytes[i + 2]! << 8) + bytes[i + 3]!
  try {
    if (ext === 'png' && bytes.length >= 24) return { width: u32be(16), height: u32be(20) }
    if (ext === 'gif' && bytes.length >= 10) return { width: u16le(6), height: u16le(8) }
    if (ext === 'webp' && bytes.length >= 30) {
      const chunk = String.fromCharCode(bytes[12]!, bytes[13]!, bytes[14]!, bytes[15]!)
      if (chunk === 'VP8X') return { width: 1 + (bytes[24]! | (bytes[25]! << 8) | (bytes[26]! << 16)), height: 1 + (bytes[27]! | (bytes[28]! << 8) | (bytes[29]! << 16)) }
      if (chunk === 'VP8 ') return { width: u16le(26) & 0x3fff, height: u16le(28) & 0x3fff }
      if (chunk === 'VP8L') {
        const b = bytes
        return { width: 1 + (((b[22]! & 0x3f) << 8) | b[21]!), height: 1 + (((b[24]! & 0x0f) << 10) | (b[23]! << 2) | ((b[22]! & 0xc0) >> 6)) }
      }
      return null
    }
    if (ext === 'jpg') {
      let i = 2
      while (i + 9 < bytes.length) {
        if (bytes[i] !== 0xff) return null
        const marker = bytes[i + 1]!
        if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) return { width: u16be(i + 7), height: u16be(i + 5) }
        i += 2 + u16be(i + 2)
      }
    }
  } catch {
    return null
  }
  return null
}

// ------------------------------------------------------------- tokenizer

type Tok = { t: 'text'; v: string } | { t: 'open'; name: string; attrs: [string, string][]; selfClose: boolean } | { t: 'close'; name: string } | { t: 'raw'; name: string; v: string } | { t: 'comment'; conditional: boolean }

// Elements whose content is text up to their own end tag.
const RAW_TEXT = new Set(['script', 'style', 'textarea', 'title', 'xmp', 'iframe', 'noembed', 'noframes', 'noscript'])
const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr', 'basefont', 'frame', 'keygen'])

function tokenize(s: string): Tok[] {
  const out: Tok[] = []
  let i = 0
  const n = s.length
  while (i < n) {
    const lt = s.indexOf('<', i)
    if (lt < 0) {
      out.push({ t: 'text', v: s.slice(i) })
      break
    }
    if (lt > i) out.push({ t: 'text', v: s.slice(i, lt) })
    if (s.startsWith('<!--', lt)) {
      const end = s.indexOf('-->', lt + 4)
      // <!--[if mso]>…<![endif]--> hides markup for Outlook only (dropped and
      // counted). <!--[if !mso]><!--> … <!--<![endif]--> is the opposite: the
      // markup between is for every other client and isn't inside a comment
      // at all, so it is kept and not counted.
      const head = s.slice(lt, lt + 24)
      out.push({ t: 'comment', conditional: /^<!--\[if/i.test(head) && !/^<!--\[if\s+!mso\]><!-->/i.test(head) })
      i = end < 0 ? n : end + 3
      continue
    }
    if (s.startsWith('<![', lt)) {
      // CDATA, or the end of a downlevel-revealed conditional (<![endif]>).
      const end = s.indexOf('>', lt)
      out.push({ t: 'comment', conditional: true })
      i = end < 0 ? n : end + 1
      continue
    }
    const next = s[lt + 1]
    if (next === '!' || next === '?') {
      const end = s.indexOf('>', lt)
      i = end < 0 ? n : end + 1
      continue
    }
    if (next === '/') {
      const m = /^<\/([a-zA-Z][a-zA-Z0-9:_-]*)[^>]*>?/.exec(s.slice(lt, lt + 300))
      if (m) {
        out.push({ t: 'close', name: m[1]!.toLowerCase() })
        // A </name with no closing > runs to the next >.
        const end = m[0].endsWith('>') ? lt + m[0].length : s.indexOf('>', lt)
        i = end < 0 ? n : m[0].endsWith('>') ? end : end + 1
      } else {
        const end = s.indexOf('>', lt)
        i = end < 0 ? n : end + 1
      }
      continue
    }
    if (!next || !/[a-zA-Z]/.test(next)) {
      out.push({ t: 'text', v: '<' })
      i = lt + 1
      continue
    }
    // A start tag.
    let j = lt + 1
    while (j < n && /[a-zA-Z0-9:_-]/.test(s[j]!)) j += 1
    const name = s.slice(lt + 1, j).toLowerCase()
    const attrs: [string, string][] = []
    let selfClose = false
    for (;;) {
      while (j < n && /[\s/]/.test(s[j]!)) {
        if (s[j] === '/' && s[j + 1] === '>') selfClose = true
        j += 1
      }
      if (j >= n) break
      if (s[j] === '>') {
        j += 1
        break
      }
      const a = j
      while (j < n && !/[\s"'>/=]/.test(s[j]!)) j += 1
      if (j === a) {
        // A stray quote or = where a name should be: skip it.
        j += 1
        continue
      }
      const attrName = s.slice(a, j).toLowerCase()
      while (j < n && /\s/.test(s[j]!)) j += 1
      let value = ''
      if (s[j] === '=') {
        j += 1
        while (j < n && /\s/.test(s[j]!)) j += 1
        const q = s[j]
        if (q === '"' || q === "'") {
          const end = s.indexOf(q, j + 1)
          value = s.slice(j + 1, end < 0 ? n : end)
          j = end < 0 ? n : end + 1
        } else {
          const b = j
          while (j < n && !/[\s>]/.test(s[j]!)) j += 1
          value = s.slice(b, j)
        }
      }
      attrs.push([attrName, value])
    }
    out.push({ t: 'open', name, attrs, selfClose })
    if (RAW_TEXT.has(name) && !selfClose) {
      const re = new RegExp(`</${name}[\\s>/]`, 'i')
      const rest = s.slice(j)
      const m = re.exec(rest)
      const body = m ? rest.slice(0, m.index) : rest
      out.push({ t: 'raw', name, v: body })
      out.push({ t: 'close', name })
      if (!m) {
        i = n
      } else {
        const end = s.indexOf('>', j + m.index)
        i = end < 0 ? n : end + 1
      }
      continue
    }
    i = j
  }
  return out
}

// ------------------------------------------------------------- entities

const NAMED: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0', colon: ':', tab: '\t', newline: '\n', lpar: '(', rpar: ')', sol: '/', bsol: '\\', period: '.', comma: ',', semi: ';', excl: '!', num: '#', copy: '©', reg: '®', trade: '™', hellip: '…', mdash: '—', ndash: '–', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', bull: '•', middot: '·', zwnj: '\u200c', zwj: '\u200d', shy: '\u00ad' }

// Entities decoded, for reading a value (a link, a URL in a style).
export function decodeEntities(v: string): string {
  return v.replace(/&(#\d{1,8}|#x[0-9a-f]{1,7}|[a-z][a-z0-9]{1,31});?/gi, (all, ent: string) => {
    if (ent[0] === '#') {
      const code = ent[1] === 'x' || ent[1] === 'X' ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10)
      return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : ''
    }
    return NAMED[ent.toLowerCase()] ?? all
  })
}

const escapeAttr = (v: string) => v.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!)
// Text as it came, with entity-looking runs kept and anything that could
// start markup escaped.
const safeText = (v: string) => v.replace(/&(?!(#\d{1,8}|#x[0-9a-f]{1,7}|[a-z][a-z0-9]{1,31});)/gi, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// ---------------------------------------------------------- Mailchimp tags

// Canva's Mailchimp publish uses Mailchimp merge tags. Hub keeps its own
// footer and its own unsubscribe, so each is mapped to Hub's equivalent or
// removed; a tag Hub doesn't know is removed and reported.
//   value: a Hub merge field, '' to remove, or a function of the moment.
const MAILCHIMP_TAGS: Record<string, string> = {
  UNSUB: '{{preference_url}}',
  UPDATE_PROFILE: '{{preference_url}}',
  FNAME: '{{first_name}}',
  'MERGE1': '{{first_name}}',
  // The footer carries all of these.
  'LIST:ADDRESS': '',
  'LIST:ADDRESSLINE': '',
  'HTML:LIST_ADDRESS_HTML': '',
  'HTML:LIST_ADDRESS': '',
  // The brand's legal name (comms_brand), filled in when the email renders.
  'LIST:COMPANY': '{{brand_legal_name}}',
  'LIST:DESCRIPTION': '',
  'LIST:NAME': '',
  'LIST:URL': '',
  'LIST:PHONE': '',
  'LIST:EMAIL': '',
  REWARDS: '',
  'HTML:REWARDS': '',
  REWARDS_TEXT: '',
  ABOUT_LIST: '',
  // No web version, no forwarding page: links to them would be dead.
  ARCHIVE: '',
  ARCHIVE_PAGE: '',
  FORWARD: '',
  'MC:SUBJECT': '',
  MC_SUBJECT: '',
  MC_PREVIEW_TEXT: '',
  EMAIL: '',
  LNAME: '',
  'MERGE2': '',
}

// The marker a dead Mailchimp link gets; the sanitizer drops the link and its words.
export const DEAD_LINK = 'hub:dead-link'

export type MailchimpReport = { mapped: Record<string, number>; removed: Record<string, number>; unknown: Record<string, number>; conditionals: number }

// Canva's own placeholders in an email export (seen in a real "Download HTML
// and images" zip, 2026-10-15): {{ unsubscribe }}, {{ update_preferences }}
// and {{ view_in_browser }}, as link targets. They look like Hub's merge
// fields but aren't; each maps to Hub's equivalent or goes.
const CANVA_TAGS: Record<string, string> = {
  unsubscribe: '{{preference_url}}',
  unsubscribe_url: '{{preference_url}}',
  update_preferences: '{{preference_url}}',
  manage_preferences: '{{preference_url}}',
  preferences: '{{preference_url}}',
  view_in_browser: '',
  web_version: '',
}
const CANVA_DEAD_LINKS = /^(view_in_browser|web_version)$/

// The legal-name field: only in imported designs, filled from comms_brand.
export const BRAND_LEGAL_NAME_FIELD = 'brand_legal_name'

// Applied to the raw HTML before cleaning. CURRENT_YEAR becomes the year of
// the upload (and is reported), since Hub has no year merge field.
export function replaceMailchimpTags(html: string, year: number): { html: string; report: MailchimpReport } {
  const report: MailchimpReport = { mapped: {}, removed: {}, unknown: {}, conditionals: 0 }
  const bump = (m: Record<string, number>, k: string) => (m[k] = (m[k] ?? 0) + 1)
  // A link to a Mailchimp page Hub has no equivalent of (the web version,
  // forward to a friend) goes with its words: "View this email in your
  // browser" with no link would only confuse.
  const canvaDead = html.replace(/href\s*=\s*(["'])\s*\{\{\s*([a-z_]+)\s*\}\}\s*\1/gi, (all, _q: string, tag: string) => {
    if (!CANVA_DEAD_LINKS.test(tag.toLowerCase())) return all
    bump(report.removed, `{{ ${tag.toLowerCase()} }}`)
    return `href="${DEAD_LINK}"`
  })
  const canva = canvaDead.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (all, tag: string) => {
    const key = tag.toLowerCase()
    if (!(key in CANVA_TAGS)) return all
    const to = CANVA_TAGS[key]!
    bump(to ? report.mapped : report.removed, `{{ ${key} }}`)
    return to
  })
  const deadLinks = canva.replace(/href\s*=\s*(["']?)\s*\*\|\s*(ARCHIVE|ARCHIVE_PAGE|FORWARD|ABOUT_LIST|REWARDS)\s*\|\*\s*\1/gi, (_all, _q: string, tag: string) => {
    bump(report.removed, tag.toUpperCase())
    return `href="${DEAD_LINK}"`
  })
  const out = deadLinks.replace(/\*(?:\||&#124;|&vert;|%7C)\s*([^|*<>]{1,60}?)\s*(?:\||&#124;|&vert;|%7C)\*/gi, (_all, rawTag: string) => {
    const tag = rawTag.trim().toUpperCase()
    if (/^(IF|IFNOT|ELSEIF|ELSE|END:IF|INTERESTED|END:INTERESTED|DATE|FEEDBLOCK|FEEDITEMS|END:FEEDITEMS|RSS|RSSITEMS|END:RSSITEMS)\b/.test(tag) || tag.startsWith('IF:') || tag.startsWith('IFNOT:') || tag.startsWith('ELSEIF:')) {
      report.conditionals += 1
      return ''
    }
    if (tag === 'CURRENT_YEAR') {
      bump(report.mapped, tag)
      return String(year)
    }
    if (tag in MAILCHIMP_TAGS) {
      const to = MAILCHIMP_TAGS[tag]!
      bump(to ? report.mapped : report.removed, tag)
      return to
    }
    bump(report.unknown, tag)
    return ''
  })
  return { html: out, report }
}

// ----------------------------------------------------------- the sanitizer

// Kept as they are (attributes filtered).
const KEEP = new Set([
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'colgroup', 'col', 'caption',
  'div', 'span', 'p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'a', 'img', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'small', 'big', 'sup', 'sub', 'center', 'font',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd', 'blockquote', 'pre', 'code', 'tt', 'address', 'abbr', 'cite', 'q', 'mark', 'del', 'ins', 'wbr',
  'section', 'article', 'header', 'footer', 'main', 'aside', 'nav', 'figure', 'figcaption',
])
// Dropped with everything inside them.
const DROP_WITH_CONTENT = new Set(['script', 'iframe', 'object', 'embed', 'applet', 'form', 'noscript', 'template', 'svg', 'math', 'video', 'audio', 'canvas', 'select', 'textarea', 'button', 'frameset', 'frame', 'noembed', 'noframes', 'xmp', 'plaintext', 'map', 'datalist', 'dialog', 'portal', 'slot'])
// Dropped (void or harmless containers handled elsewhere).
const DROP_TAG = new Set(['meta', 'link', 'base', 'input', 'param', 'source', 'track', 'area', 'keygen', 'picture', 'option', 'optgroup', 'label', 'fieldset', 'legend', 'output', 'progress', 'meter'])

// What a browser keeps in the head (anything else starts the body).
const HEAD_ONLY = new Set(['title', 'style', 'meta', 'link', 'base', 'script', 'noscript', 'html', 'head', 'xml'])
const DROP_LABELS: Record<string, string> = {
  script: 'script',
  iframe: 'embedded frame (iframe)',
  object: 'embedded object',
  embed: 'embedded object',
  applet: 'embedded object',
  form: 'form',
  select: 'form',
  button: 'form',
  textarea: 'form',
  svg: 'SVG drawing',
  video: 'video or audio',
  audio: 'video or audio',
}

// Attributes any kept element may carry, and per element.
const GLOBAL_ATTRS = new Set(['style', 'class', 'id', 'align', 'valign', 'width', 'height', 'bgcolor', 'dir', 'lang', 'title', 'role', 'aria-hidden', 'aria-label'])
const ELEMENT_ATTRS: Record<string, Set<string>> = {
  table: new Set(['border', 'cellpadding', 'cellspacing', 'background', 'summary', 'frame', 'rules']),
  td: new Set(['colspan', 'rowspan', 'nowrap', 'background', 'scope', 'headers', 'abbr']),
  th: new Set(['colspan', 'rowspan', 'nowrap', 'background', 'scope', 'headers', 'abbr']),
  tr: new Set(['background']),
  col: new Set(['span']),
  colgroup: new Set(['span']),
  a: new Set(['href', 'name']),
  img: new Set(['src', 'alt', 'border', 'hspace', 'vspace']),
  font: new Set(['color', 'face', 'size']),
  ol: new Set(['start', 'type', 'reversed']),
  ul: new Set(['type']),
  li: new Set(['value', 'type']),
  hr: new Set(['size', 'noshade', 'color']),
}
// Plain values: sizes, colors, alignments, names. No quotes, parens,
// colons, semicolons or angle brackets.
const PLAIN_VALUE = /^[#%\w\s.,+-]{0,80}$/

export type SanitizeReport = {
  // What was taken out, by kind, for the staff report.
  removed: Record<string, number>
  // Images left out: on another site (never loaded from there), or not in the zip.
  remoteImages: number
  missingImages: string[]
  fontsDropped: number
}

export type SanitizeOptions = {
  // An image reference (decoded) to the hosted URL Hub serves it from, or
  // null to leave it out. Called for <img src>, background="" and url() in
  // styles.
  resolveImage: (ref: string) => string | null
  // 'import': the first pass on a design (classes and ids get the d- prefix,
  // style rules are scoped under .hub-design). 'verify': core's second pass
  // on what the first pass made (already prefixed and scoped); rules that
  // aren't scoped are dropped.
  mode: 'import' | 'verify'
}

export type SanitizedHtml = {
  html: string
  styles: string
  // The design's own body background and padding, on the wrapper Hub puts
  // it in.
  wrapperStyle: string
  title: string | null
  report: SanitizeReport
}

export const DESIGN_SCOPE = '.hub-design'

// When a dead link goes with its words ("View in browser"), the separator
// written just before it (" | " in "Unsubscribe | Preferences | View in
// browser") goes too, looking back past tags only.
const SEPARATOR_ONLY = /^(\s|&nbsp;|&#160;|\||·|&middot;|•|&bull;|–|—|-)+$/
function dropSeparatorBefore(out: string[]) {
  for (let k = out.length - 1, seen = 0; k >= 0 && seen < 6; k -= 1, seen += 1) {
    const piece = out[k]!
    if (piece.startsWith('<')) continue
    if (SEPARATOR_ONLY.test(piece)) {
      out[k] = ''
      return
    }
    const trimmed = piece.replace(/(\s|&nbsp;)*(\||·|&middot;|•|&bull;)(\s|&nbsp;)*$/, '')
    if (trimmed !== piece) out[k] = trimmed
    return
  }
}

function bump(map: Record<string, number>, key: string, by = 1) {
  map[key] = (map[key] ?? 0) + by
}

// A link a reader may follow: web, email or phone; or Hub's link merge
// fields standing alone. Anything else (javascript:, data:, relative, #)
// is dropped and the text stays.
export function safeLink(raw: string): string | null {
  const v = decodeEntities(raw).replace(/[\u0000-\u0020\u007f-\u009f\u200b-\u200f\u2028\u2029\ufeff]/g, '')
  if (/^\{\{(mybre_url|preference_url)\}\}$/.test(v)) return v
  if (/^https?:\/\/[^\s<>"'`\\]+$/i.test(v)) return v
  if (/^mailto:[^\s<>"'`\\]+$/i.test(v)) return v
  // A phone link needs a phone number: Canva (or a mail app) sometimes links
  // a date like "2027 - 7" as tel:20277.
  if (/^tel:[+0-9().\-]{3,40}$/i.test(v) && v.replace(/\D/g, '').length >= 7) return v
  return null
}

const prefixName = (v: string, mode: SanitizeOptions['mode']) => (mode === 'import' ? `d-${v}` : v)

export function sanitizeEmailHtml(input: string, opts: SanitizeOptions): SanitizedHtml {
  const report: SanitizeReport = { removed: {}, remoteImages: 0, missingImages: [], fontsDropped: 0 }
  const toks = tokenize(input)
  const out: string[] = []
  const styles: string[] = []
  let title: string | null = null
  let wrapperStyle = ''
  // The open elements: whether each was written out (unwrapped ones weren't).
  const stack: { name: string; emitted: boolean }[] = []
  let skip: { name: string; depth: number } | null = null
  // Inside <head>: nothing is written but the title and styles are read.
  // Anything that belongs in a body ends the head, as in a browser.
  let inHead = false
  const missing = new Set<string>()

  const resolveImage = (ref: string, what: string): string | null => {
    const v = decodeEntities(ref).trim()
    if (!v) return null
    const url = opts.resolveImage(v)
    if (url) return url
    if (/^(https?:)?\/\//i.test(v)) {
      report.remoteImages += 1
      bump(report.removed, 'image on another site')
    } else if (/^data:/i.test(v)) {
      bump(report.removed, 'embedded (data:) image that is not PNG, JPEG, GIF or WebP')
    } else if (/^(javascript|vbscript):/i.test(v.replace(/\s/g, ''))) {
      bump(report.removed, 'script link')
    } else {
      missing.add(v.slice(0, 200))
      bump(report.removed, `${what} not in the zip`)
    }
    return null
  }

  for (const tok of toks) {
    if (skip) {
      if (tok.t === 'open' && tok.name === skip.name && !tok.selfClose && !VOID.has(tok.name)) skip.depth += 1
      if (tok.t === 'close' && tok.name === skip.name) {
        skip.depth -= 1
        if (skip.depth === 0) skip = null
      }
      continue
    }
    if (tok.t === 'comment') {
      if (tok.conditional) bump(report.removed, 'Outlook-only markup (other mail apps never show it)')
      continue
    }
    if (tok.t === 'text') {
      if (inHead) continue
      out.push(safeText(tok.v))
      continue
    }
    if (tok.t === 'raw') {
      if (tok.name === 'style') {
        const css = sanitizeStylesheet(tok.v, { resolveImage: (r) => resolveImage(r, 'background image'), mode: opts.mode, report })
        if (css) styles.push(css)
      } else if (tok.name === 'title') {
        const t = decodeEntities(tok.v).replace(/\s+/g, ' ').trim()
        if (t && title === null) title = t.slice(0, 150)
      }
      continue
    }
    if (tok.t === 'close') {
      const name = tok.name
      if (name === 'head') inHead = false
      if (name === 'head' || name === 'html' || name === 'body') continue
      const at = stack.map((s) => s.name).lastIndexOf(name)
      // A stray end tag (nothing of that name is open) is dropped, so a
      // design can never close the wrapper Hub puts it in.
      if (at < 0) continue
      for (let k = stack.length - 1; k >= at; k -= 1) if (stack[k]!.emitted) out.push(`</${stack[k]!.name}>`)
      stack.length = at
      continue
    }
    // An open tag.
    const name = tok.name
    if (name === 'html') continue
    if (name === 'head') {
      inHead = !tok.selfClose
      continue
    }
    if (!HEAD_ONLY.has(name)) inHead = false
    if (name === 'body') {
      // The body's own look goes on the wrapper.
      const parts: string[] = []
      for (const [k, v] of tok.attrs) {
        if (k === 'style') {
          const s = sanitizeDeclarations(decodeEntities(v), (r) => resolveImage(r, 'background image'), report)
          if (s) parts.push(s)
        } else if (k === 'bgcolor' && PLAIN_VALUE.test(v)) parts.push(`background-color:${v.trim()}`)
        else if (k.startsWith('on')) bump(report.removed, 'event handler (on…)')
      }
      wrapperStyle = parts.join(';')
      continue
    }
    if (name === 'meta') {
      if (tok.attrs.some(([k, v]) => k === 'http-equiv' && /refresh/i.test(v))) bump(report.removed, 'automatic redirect (meta refresh)')
      continue
    }
    if (name === 'link') {
      const rel = tok.attrs.find(([k]) => k === 'rel')?.[1] ?? ''
      bump(report.removed, /stylesheet/i.test(rel) ? 'linked stylesheet on another site' : 'linked resource')
      continue
    }
    if (name === 'base') {
      bump(report.removed, 'base address')
      continue
    }
    if (DROP_WITH_CONTENT.has(name)) {
      bump(report.removed, DROP_LABELS[name] ?? name)
      if (!tok.selfClose && !VOID.has(name)) skip = { name, depth: 1 }
      continue
    }
    if (DROP_TAG.has(name)) {
      if (name === 'input' || name === 'label' || name === 'fieldset') bump(report.removed, 'form')
      if (name === 'source' || name === 'picture') bump(report.removed, 'responsive image source')
      if (!VOID.has(name) && !tok.selfClose) stack.push({ name, emitted: false })
      continue
    }
    if (name === 'style' || name === 'title') continue
    if (!KEEP.has(name)) {
      // Unknown or namespaced (o:p, v:rect): the tag goes, its content stays.
      if (!VOID.has(name) && !tok.selfClose) stack.push({ name, emitted: false })
      continue
    }

    // A kept element: only the attributes named above, each checked.
    const allowed = ELEMENT_ATTRS[name]
    const attrs: string[] = []
    let dropElement = false
    let unwrap = false
    let hasAlt = false
    for (const [k, rawV] of tok.attrs) {
      if (k.startsWith('on')) {
        bump(report.removed, 'event handler (on…)')
        continue
      }
      if (!GLOBAL_ATTRS.has(k) && !allowed?.has(k)) {
        if (k === 'srcset') bump(report.removed, 'responsive image source')
        if (k === 'action' || k === 'formaction') bump(report.removed, 'form')
        continue
      }
      const v = decodeEntities(rawV)
      if (k === 'style') {
        const s = sanitizeDeclarations(v, (r) => resolveImage(r, 'background image'), report)
        if (s) attrs.push(`style="${escapeAttr(s)}"`)
      } else if (k === 'class' || k === 'id') {
        const names = v.split(/\s+/).filter((c) => /^-?[A-Za-z_][\w-]{0,60}$/.test(c))
        if (names.length) attrs.push(`${k}="${escapeAttr(names.map((c) => prefixName(c, opts.mode)).join(' '))}"`)
      } else if (k === 'href') {
        if (v === DEAD_LINK) {
          dropElement = true
          continue
        }
        const link = safeLink(v)
        if (link) attrs.unshift(`href="${escapeAttr(link)}"`)
        else if (v.trim() && !v.trim().startsWith('#')) bump(report.removed, /^\s*(javascript|vbscript|data):/i.test(v.replace(/\s/g, '')) ? 'script or data link' : /^\s*tel:/i.test(v) ? 'phone link that isn’t a phone number' : 'link that isn’t http(s), mailto or tel')
      } else if (k === 'src') {
        const url = resolveImage(v, 'image')
        if (!url) dropElement = true
        else attrs.unshift(`src="${escapeAttr(url)}"`)
      } else if (k === 'background') {
        const url = resolveImage(v, 'background image')
        if (url) attrs.push(`background="${escapeAttr(url)}"`)
      } else if (k === 'alt' || k === 'title' || k === 'aria-label' || k === 'summary' || k === 'abbr') {
        if (k === 'alt') hasAlt = true
        attrs.push(`${k}="${escapeAttr(v.replace(/[\u0000-\u001f]/g, ' ').slice(0, 500))}"`)
      } else if (k === 'name') {
        if (/^[\w-]{1,60}$/.test(v)) attrs.push(`name="${v}"`)
      } else if (k === 'dir') {
        if (/^(ltr|rtl|auto)$/i.test(v)) attrs.push(`dir="${v.toLowerCase()}"`)
      } else if (k === 'role') {
        if (/^[a-z]{1,20}$/.test(v)) attrs.push(`role="${v}"`)
      } else if (k === 'nowrap' || k === 'noshade' || k === 'reversed') {
        attrs.push(k)
      } else if (k === 'face') {
        if (/^[\w\s,'-]{1,120}$/.test(v)) attrs.push(`face="${escapeAttr(v)}"`)
      } else if (PLAIN_VALUE.test(v)) {
        attrs.push(`${k}="${escapeAttr(v.trim())}"`)
      }
    }
    if (name === 'img') {
      if (!tok.attrs.some(([k]) => k === 'src')) dropElement = true
      if (dropElement) continue
      if (!hasAlt) attrs.push('alt=""')
    }
    if (name === 'a' && dropElement) {
      if (!tok.selfClose) skip = { name: 'a', depth: 1 }
      dropSeparatorBefore(out)
      continue
    }
    if (name === 'a' && !attrs.some((a) => a.startsWith('href='))) unwrap = true
    if (unwrap) {
      // A link with nowhere safe to go is its text.
      stack.push({ name, emitted: false })
      continue
    }
    out.push(`<${name}${attrs.length ? ` ${attrs.join(' ')}` : ''}>`)
    if (!VOID.has(name) && !tok.selfClose) stack.push({ name, emitted: true })
  }
  for (let k = stack.length - 1; k >= 0; k -= 1) if (stack[k]!.emitted) out.push(`</${stack[k]!.name}>`)
  report.missingImages = [...missing]
  return { html: out.join('').trim(), styles: styles.join('\n'), wrapperStyle, title, report }
}

// ------------------------------------------------------------------- CSS

const BAD_CSS = /expression\s*\(|javascript:|vbscript:|behaviou?r\s*:|-moz-binding|@import|<|\\|\/\*|-ms-filter|filter\s*:\s*progid/i

function splitTop(s: string, sep: string): string[] {
  const parts: string[] = []
  let depth = 0
  let quote: string | null = null
  let cur = ''
  for (const ch of s) {
    if (quote) {
      if (ch === quote) quote = null
    } else if (ch === '"' || ch === "'") quote = ch
    else if (ch === '(') depth += 1
    else if (ch === ')') depth = Math.max(0, depth - 1)
    else if (ch === sep && depth === 0) {
      parts.push(cur)
      cur = ''
      continue
    }
    cur += ch
  }
  parts.push(cur)
  return parts
}

// One style="" (or one rule's body): known-safe declarations only. A url()
// is kept only when it resolves to an image Hub hosts.
export function sanitizeDeclarations(css: string, resolveImage: (ref: string) => string | null, report?: SanitizeReport): string {
  const out: string[] = []
  for (const decl of splitTop(css.replace(/\/\*[\s\S]*?\*\//g, ''), ';')) {
    const colon = decl.indexOf(':')
    if (colon < 0) continue
    const prop = decl.slice(0, colon).trim().toLowerCase()
    let value = decl.slice(colon + 1).trim()
    if (!/^-?[a-z][a-z0-9-]{0,60}$/.test(prop) || !value || value.length > 2000) continue
    if (BAD_CSS.test(value) || /[{}]/.test(value)) {
      if (report) bump(report.removed, 'unsafe style')
      continue
    }
    // Nothing in a design may sit over Hub's footer.
    if (prop === 'position' && !/^(static|relative)\b/i.test(value)) {
      if (report) bump(report.removed, 'positioned layer')
      continue
    }
    if (prop === 'behavior' || prop === 'z-index') continue
    let bad = false
    value = value.replace(/url\(\s*(['"]?)(.*?)\1\s*\)/gi, (_all, _q: string, ref: string) => {
      const url = resolveImage(ref)
      if (!url) {
        bad = true
        return ''
      }
      return `url(${url})`
    })
    if (bad) continue
    // A function other than these is dropped (no url() left by now).
    const fns = [...value.matchAll(/([a-z-]+)\s*\(/gi)].map((m) => m[1]!.toLowerCase())
    if (fns.some((f) => !['url', 'rgb', 'rgba', 'hsl', 'hsla', 'calc', 'linear-gradient', 'radial-gradient', 'min', 'max', 'clamp'].includes(f))) continue
    if (/["']/.test(value.replace(/url\([^)]*\)/g, '')) && !/^font-family$/.test(prop)) continue
    out.push(`${prop}:${value}`)
  }
  return out.join(';')
}

// A selector, scoped under the design's wrapper, its classes and ids
// prefixed as the elements' were. Null drops the rule.
function scopeSelector(sel: string, mode: SanitizeOptions['mode']): string | null {
  const s = sel.trim().replace(/\s+/g, ' ')
  if (!s || s.length > 300 || !/^[\w\s.#:,>+~*[\]="'()|^$-]+$/.test(s)) return null
  if (/:root/i.test(s)) return null
  if (mode === 'verify') {
    return s === DESIGN_SCOPE || s.startsWith(`${DESIGN_SCOPE} `) || s.startsWith(`${DESIGN_SCOPE}:`) || s.startsWith(`${DESIGN_SCOPE}.`) ? s : null
  }
  const renamed = s.replace(/([.#])(-?[A-Za-z_][\w-]*)/g, (_m, sigil: string, name: string) => `${sigil}d-${name}`)
  const head = /^(html\b\s*>?\s*)?(body\b)?/i.exec(renamed)
  if (head && head[0].trim()) {
    const rest = renamed.slice(head[0].length).trim()
    return rest ? `${DESIGN_SCOPE} ${rest.replace(/^>\s*/, '')}` : DESIGN_SCOPE
  }
  return `${DESIGN_SCOPE} ${renamed}`
}

// A whole stylesheet: rules (and @media blocks of rules) only, every
// selector scoped. @import, @font-face, @charset and everything else go.
export function sanitizeStylesheet(css: string, opts: { resolveImage: (ref: string) => string | null; mode: SanitizeOptions['mode']; report: SanitizeReport }): string {
  const src = css.replace(/\/\*[\s\S]*?\*\//g, '').replace(/<!--|-->/g, '')
  const out: string[] = []
  const rules = (text: string, depth: number): string[] => {
    const acc: string[] = []
    let i = 0
    while (i < text.length) {
      const brace = text.indexOf('{', i)
      const semi = text.indexOf(';', i)
      if (brace < 0) break
      // A statement at-rule (@import url(...); @charset ...;) before the next block.
      if (semi >= 0 && semi < brace && /^\s*@/.test(text.slice(i, semi))) {
        const at = text.slice(i, semi).trim().toLowerCase()
        if (at.startsWith('@import')) bump(opts.report.removed, 'linked stylesheet on another site')
        i = semi + 1
        continue
      }
      const prelude = text.slice(i, brace).trim()
      // The matching close brace.
      let d = 1
      let j = brace + 1
      while (j < text.length && d > 0) {
        if (text[j] === '{') d += 1
        else if (text[j] === '}') d -= 1
        j += 1
      }
      const body = text.slice(brace + 1, j - 1)
      i = j
      if (prelude.startsWith('@')) {
        const atName = /^@([a-z-]+)/i.exec(prelude)?.[1]?.toLowerCase() ?? ''
        if (atName === 'media' && depth === 0) {
          const query = prelude.slice(6).trim()
          if (/^[a-z0-9\s(),:.-]{1,200}$/i.test(query)) {
            const inner = rules(body, 1)
            if (inner.length) acc.push(`@media ${query}{${inner.join('')}}`)
          }
        } else if (atName === 'font-face') {
          opts.report.fontsDropped += 1
        } else {
          bump(opts.report.removed, `@${atName} rule`)
        }
        continue
      }
      const selectors = prelude.split(',').map((s) => scopeSelector(s, opts.mode)).filter((s): s is string => !!s)
      if (!selectors.length) continue
      const decls = sanitizeDeclarations(body, opts.resolveImage, opts.report)
      if (decls) acc.push(`${selectors.join(',')}{${decls}}`)
    }
    return acc
  }
  out.push(...rules(src, 0))
  return out.join('\n')
}

// Every image reference in a design: <img src>, background="", and url()
// in style="" and <style>. The web Worker hosts each before cleaning.
export function imageRefs(html: string): string[] {
  const refs = new Set<string>()
  const fromCss = (css: string) => {
    for (const m of css.matchAll(/url\(\s*(['"]?)(.*?)\1\s*\)/gi)) if (m[2]) refs.add(decodeEntities(m[2]).trim())
  }
  for (const tok of tokenize(html)) {
    if (tok.t === 'raw' && tok.name === 'style') fromCss(tok.v)
    if (tok.t !== 'open') continue
    for (const [k, v] of tok.attrs) {
      if ((k === 'src' && tok.name === 'img') || k === 'background') refs.add(decodeEntities(v).trim())
      if (k === 'style') fromCss(decodeEntities(v))
    }
  }
  refs.delete('')
  return [...refs]
}

// ------------------------------------------------------------ plain text

const BLOCK = new Set(['p', 'div', 'table', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'section', 'article', 'header', 'footer', 'center', 'hr', 'address', 'figure', 'figcaption', 'dl', 'dt', 'dd'])

// The plain-text part from cleaned HTML: headings, paragraphs, list items,
// and each link's address after its text. Staff can edit it before saving.
export function htmlToText(html: string): string {
  const lines: string[] = []
  let cur = ''
  const links: { href: string; start: number }[] = []
  let heading = false
  const flush = (blank = true) => {
    const t = cur.replace(/[ \t\u00a0]+/g, ' ').trim()
    if (t) lines.push(...(heading ? [t, '-'.repeat(Math.min(t.length, 60))] : [t]))
    else if (blank && lines.length && lines[lines.length - 1] !== '') lines.push('')
    cur = ''
  }
  for (const tok of tokenize(html)) {
    if (tok.t === 'text') cur += decodeEntities(tok.v).replace(/\s+/g, ' ')
    else if (tok.t === 'open') {
      if (tok.name === 'br') {
        const t = cur.replace(/[ \t\u00a0]+/g, ' ').trim()
        if (t) lines.push(t)
        cur = ''
      } else if (BLOCK.has(tok.name) || /^h[1-6]$/.test(tok.name)) {
        flush(tok.name !== 'li')
        if (/^h[1-6]$/.test(tok.name)) heading = true
        if (tok.name === 'li') cur = '- '
        if (tok.name === 'hr') lines.push('* * *', '')
      } else if (tok.name === 'a') {
        links.push({ href: tok.attrs.find(([k]) => k === 'href')?.[1] ?? '', start: cur.length })
      } else if (tok.name === 'img') {
        const alt = decodeEntities(tok.attrs.find(([k]) => k === 'alt')?.[1] ?? '').trim()
        if (alt) cur += ` ${alt} `
      }
    } else if (tok.t === 'close') {
      if (tok.name === 'a') {
        const l = links.pop()
        const href = l ? decodeEntities(l.href) : ''
        const label = l ? cur.slice(l.start).trim() : ''
        if (href && !href.startsWith('mailto:') && label !== href) cur += ` (${href})`
        else if (href.startsWith('mailto:') && !label.includes(href.slice(7))) cur += ` (${href.slice(7)})`
      } else if (BLOCK.has(tok.name) || /^h[1-6]$/.test(tok.name)) {
        flush(tok.name !== 'li')
        if (/^h[1-6]$/.test(tok.name)) {
          heading = false
          lines.push('')
        }
        if (tok.name === 'p') lines.push('')
      }
    }
  }
  flush()
  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, HTML_IMPORT_LIMITS.textChars)
}

// ------------------------------------------------------------- analysis

export type HtmlAnalysis = { images: number; imagesMissingAlt: number; links: number; words: number; mergeFields: string[]; mailchimpTags: string[] }

export function analyzeHtml(html: string): HtmlAnalysis {
  let images = 0
  let imagesMissingAlt = 0
  let links = 0
  for (const tok of tokenize(html)) {
    if (tok.t !== 'open') continue
    if (tok.name === 'img') {
      images += 1
      const alt = tok.attrs.find(([k]) => k === 'alt')?.[1]?.trim() ?? ''
      const w = Number(tok.attrs.find(([k]) => k === 'width')?.[1] ?? NaN)
      const h = Number(tok.attrs.find(([k]) => k === 'height')?.[1] ?? NaN)
      // A spacer (a pixel or two) needs no words.
      const spacer = (w > 0 && w <= 2) || (h > 0 && h <= 2)
      if (!alt && !spacer) imagesMissingAlt += 1
    }
    if (tok.name === 'a' && tok.attrs.some(([k]) => k === 'href')) links += 1
  }
  const text = htmlToText(html).replace(/\([^)\s]*:\/\/[^)\s]*\)/g, ' ')
  const words = (text.match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu) ?? []).length
  const mergeFields = [...new Set([...html.matchAll(/\{\{\s*([a-z_]+)/g)].map((m) => m[1]!))]
  const mailchimpTags = [...new Set([...html.matchAll(/\*\|([^|*]{1,60})\|\*/g)].map((m) => m[1]!))]
  return { images, imagesMissingAlt, links, words, mergeFields, mailchimpTags }
}

// ------------------------------------------------------------- contents

export const EmailImageSchema = z.object({
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  ext: z.enum(EMAIL_IMAGE_EXTS),
  bytes: z.number().int().min(1).max(HTML_IMPORT_LIMITS.imageBytes),
  width: z.number().int().min(0).max(100_000).nullable(),
  height: z.number().int().min(0).max(100_000).nullable(),
  // The name it had in the zip (or "embedded"), for the report.
  name: z.string().max(300),
})
export type EmailImage = z.infer<typeof EmailImageSchema>

// What a staff member saves: the import, and the parts they edit.
export const HtmlTemplateRefSchema = z.object({
  kind: z.literal('html'),
  importId: z.string().regex(/^chi_[A-Za-z0-9]{8,40}$/),
  subject: z.string().trim().min(1).max(150),
  preheader: z.string().trim().max(200).nullable(),
  text: z.string().trim().min(1).max(HTML_IMPORT_LIMITS.textChars),
})
export type HtmlTemplateRef = z.infer<typeof HtmlTemplateRefSchema>

// What a version stores: the ref and, from the import, the cleaned design,
// so the version is complete and its hash covers exactly what is sent.
export const ImportedHtmlContentSchema = HtmlTemplateRefSchema.extend({
  html: z.string().min(1).max(HTML_IMPORT_LIMITS.storedHtmlBytes),
  styles: z.string().max(HTML_IMPORT_LIMITS.storedStyleBytes),
  wrapperStyle: z.string().max(2000),
  zipSha256: z.string().regex(/^[0-9a-f]{64}$/),
  images: z.array(EmailImageSchema).max(HTML_IMPORT_LIMITS.images),
})
export type ImportedHtmlContent = z.infer<typeof ImportedHtmlContentSchema>

// The import report the web Worker sends core with the cleaned design.
export const HtmlImportBodySchema = z.object({
  zip: z.object({
    r2Key: z.string().regex(/^comms-imports\/[0-9a-f-]{36}\.zip$/),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    bytes: z.number().int().min(1).max(HTML_IMPORT_LIMITS.zipBytes),
    filename: z.string().max(255).nullable(),
    files: z.number().int().min(1).max(HTML_IMPORT_LIMITS.entries),
  }),
  htmlPath: z.string().min(1).max(300),
  imageOrigin: z.string().regex(/^(https:\/\/[a-z0-9.-]+|http:\/\/(localhost|127\.0\.0\.1))(:\d+)?$/),
  title: z.string().max(150).nullable(),
  html: z.string().min(1).max(HTML_IMPORT_LIMITS.storedHtmlBytes),
  styles: z.string().max(HTML_IMPORT_LIMITS.storedStyleBytes),
  wrapperStyle: z.string().max(2000),
  text: z.string().min(1).max(HTML_IMPORT_LIMITS.textChars),
  images: z.array(EmailImageSchema).max(HTML_IMPORT_LIMITS.images),
  report: z.object({
    removed: z.record(z.string(), z.number().int()),
    remoteImages: z.number().int(),
    missingImages: z.array(z.string().max(200)).max(200),
    fontsDropped: z.number().int(),
    ignoredFiles: z.array(z.string().max(300)).max(400),
    // Where the plain-text part came from: the zip's own .txt (Canva ships
    // one), or made from the design.
    textFrom: z.enum(['zip', 'design']).optional(),
    textFile: z.string().max(300).nullable().optional(),
    // The text made from the design (with each link's address), kept so
    // staff can switch to it when the zip's own text has no addresses.
    designText: z.string().max(60_000).optional(),
    mailchimp: z.object({ mapped: z.record(z.string(), z.number().int()), removed: z.record(z.string(), z.number().int()), unknown: z.record(z.string(), z.number().int()), conditionals: z.number().int() }),
  }),
})
export type HtmlImportBody = z.infer<typeof HtmlImportBodySchema>
export type HtmlImportReport = HtmlImportBody['report']

export type HtmlImportView = {
  id: string
  zip: { sha256: string; bytes: number; filename: string | null; files: number }
  htmlPath: string
  title: string | null
  text: string
  images: EmailImage[]
  report: HtmlImportReport
  createdAt: string
  createdByName: string | null
}

// The merge fields in an imported design and its text, checked: from the
// fixed list only; a link field only as a whole link; nothing in styles.
export function htmlTemplateIssues(content: { subject: string; preheader: string | null; html: string; styles: string; text: string }, fields: Record<string, { url: boolean }>): string[] {
  const issues: string[] = []
  const MERGE = /\{\{\s*([a-z_]+)\s*(?:\|([^{}|]{0,40}))?\}\}/g
  const known = Object.keys(fields)
  fields = { ...fields, [BRAND_LEGAL_NAME_FIELD]: { url: false } }
  for (const [where, s] of [['subject', content.subject], ['preheader', content.preheader ?? ''], ['design', content.html], ['plain text', content.text]] as const) {
    for (const m of s.matchAll(MERGE)) if (!(m[1]! in fields)) issues.push(`{{${m[1]}}} in the ${where} isn’t a merge field Hub knows. Use one of: ${known.map((k) => `{{${k}}}`).join(', ')}`)
    if (/\{\{|\}\}/.test(s.replace(MERGE, ''))) issues.push(`A {{ or }} in the ${where} isn’t part of a well-formed merge field (type the whole field in Canva in one go, in one style)`)
    if (/\*\|[^|*]{1,60}\|\*/.test(s)) issues.push(`The ${where} still has a Mailchimp merge tag`)
  }
  for (const m of `${content.subject} ${content.preheader ?? ''}`.matchAll(MERGE)) if (fields[m[1]!]?.url) issues.push(`{{${m[1]}}} is a link and can't go in the subject or preheader`)
  for (const m of content.html.matchAll(/href="([^"]*)"/g)) {
    const href = m[1]!
    if (href.includes('{{') && !/^\{\{(mybre_url|preference_url)\}\}$/.test(href)) issues.push('A link in the design has a merge field inside it; only {{mybre_url}} or {{preference_url}} can be a whole link')
  }
  if (/\{\{/.test(content.styles)) issues.push('A merge field can’t go in a style')
  return [...new Set(issues)]
}

export type HtmlQualityWarning = { level: 'warn' | 'info'; message: string }

// The preview's quality warnings: none of them stops saving.
export function htmlQualityWarnings(content: { subject: string; preheader: string | null; html: string }, renderedBytes: number): HtmlQualityWarning[] {
  const a = analyzeHtml(content.html)
  const w: HtmlQualityWarning[] = []
  const kb = (n: number) => `${Math.round(n / 1024)} KB`
  if (renderedBytes > GMAIL_CLIP_BYTES) w.push({ level: 'warn', message: `The email is ${kb(renderedBytes)}. Gmail clips anything over about 102 KB, hiding the end of the design and the unsubscribe footer behind “View entire message”. Make the design shorter or simpler.` })
  else if (renderedBytes > SIZE_WARN_BYTES) w.push({ level: 'warn', message: `The email is ${kb(renderedBytes)}, close to Gmail’s 102 KB clipping limit.` })
  if (a.images > 0 && a.words < 50) w.push({ level: 'warn', message: `The design is mostly images (${a.images} image${a.images === 1 ? '' : 's'}, about ${a.words} words of real text). Spam filters distrust image-only email, and anyone with images off sees almost nothing. Keep headlines and key details as text in Canva, not as pictures.` })
  else if (a.images > 0 && a.words / a.images < 20) w.push({ level: 'info', message: `About ${a.words} words of text to ${a.images} images: on the image-heavy side.` })
  if (a.imagesMissingAlt > 0) w.push({ level: 'warn', message: `${a.imagesMissingAlt} image${a.imagesMissingAlt === 1 ? ' has' : 's have'} no alt text, so screen readers and people with images off get nothing for ${a.imagesMissingAlt === 1 ? 'it' : 'them'}. In Canva, select the image and add alternative text.` })
  if (a.links === 0) w.push({ level: 'info', message: 'The design has no links.' })
  else if (a.links > 40) w.push({ level: 'warn', message: `${a.links} links is a lot; spam filters notice link-heavy email.` })
  else w.push({ level: 'info', message: `${a.links} link${a.links === 1 ? '' : 's'} in the design, plus the footer’s.` })
  if (!content.preheader?.trim()) w.push({ level: 'warn', message: 'No preheader: inboxes will show the first words of the design instead (often alt text or “View in browser”).' })
  if (!content.subject.trim()) w.push({ level: 'warn', message: 'No subject.' })
  return w
}

export function isImportedHtml<T extends object>(content: T): content is Extract<T, { kind: 'html' }>
export function isImportedHtml(content: unknown): content is ImportedHtmlContent
export function isImportedHtml(content: unknown): boolean {
  return !!content && typeof content === 'object' && (content as { kind?: unknown }).kind === 'html'
}

// The environment guard (ADR 0026) for image hosting: a non-production Hub
// never writes production image addresses into a template (its images live
// in its own bucket, served from its own host), and every image address
// must be https.
export const PRODUCTION_HUB_HOSTS: readonly string[] = ['hub.bre.live', 'forms.bre.live']
export function imageOriginRefusal(origin: string | undefined, environment: string): string | null {
  let url: URL
  try {
    url = new URL(origin ?? '')
  } catch {
    return 'Hub’s public address (PUBLIC_ORIGIN) isn’t set, so images have nowhere to be served from'
  }
  // A local, non-production Hub (tests, screenshots) may serve from localhost.
  const local = environment !== 'production' && url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')
  if ((url.protocol !== 'https:' && !local) || url.pathname.replace(/\/+$/, '') !== '' || url.search || url.hash) return 'Hub’s public address must be a bare https:// origin'
  if (environment !== 'production' && PRODUCTION_HUB_HOSTS.includes(url.host)) return `This Hub isn’t production but its address is production’s (${url.host}); it won’t put production image links in a template`
  return null
}
